import Tippy from '@tippyjs/react';
import { Badge } from 'react-bootstrap';
import { Column } from 'react-table';

import { ActionsCell } from 'app/components/Table/columns/ActionsCell';
import { CustomHeader } from 'app/components/Table/columns/CustomHeader';
import { ActionTable, ID, TYPE_MODAL } from 'app/components/Table/core/types';
import Typography from 'app/components/Typography';

import { BADGE_MAP, MAIL_TYPE_LABELS } from 'app/modules/mail-history/core/constants';
import { MailContentWrapper, RecipientWrapper } from 'app/modules/mail-history/core/styles';
import {
  MailStats,
  MailTrackingRecipientType,
  MailType,
  Recipient,
} from 'app/modules/mail-history/core/types';

import { FormDate, formatCustomDate } from 'app/helpers';
import isNull from 'lodash/isNull';

import { ReactComponent as UserGroupIcon } from 'app/assets/icons/profile-2user.svg';

export const mailHistoryColumns: (
  setIsShow: React.Dispatch<
    React.SetStateAction<{ content: boolean; recipient: boolean; filter: boolean }>
  >,
  setData: React.Dispatch<
    React.SetStateAction<{ title: string; content: string; recipient: Recipient[] }>
  >,
  onResendMailTracking: (id: ID) => () => void,
) => ReadonlyArray<Column<MailStats>> = (setIsShow, setData, onResendMailTracking) => {
  const actions = (data: MailStats) => {
    const listActions: ActionTable[] = [
      {
        name: 'Resend',
        type: TYPE_MODAL.NONE,
        onClick: onResendMailTracking(data.id),
      },
    ];

    return listActions;
  };

  return [
    {
      id: 'mail_tracking.sent_at',
      width: '10%',
      Header: (props: any) => <CustomHeader tableProps={props} title="Time Sent Email" />,
      accessor: 'sentAt',
      Cell: ({ value }) => <Typography variant="body-1">{formatCustomDate(value)}</Typography>,
    },
    {
      id: 'mail_tracking.organization_name',
      width: '15%',
      Header: (props: any) => <CustomHeader tableProps={props} title="Organization" />,
      accessor: 'organization',
      Cell: ({ value }) => <Typography variant="body-1">{value.name}</Typography>,
    },
    {
      id: 'mail_tracking.type',
      width: '15%',
      Header: (props: any) => <CustomHeader tableProps={props} title="Type" />,
      accessor: 'type',
      Cell: ({ value }) => {
        const fallback = { variant: 'light', textColor: '#000' };

        const { variant, textColor } = BADGE_MAP[value as MailType] || fallback;

        const label = MAIL_TYPE_LABELS[value as MailType] || value;

        return (
          <Badge pill bg={variant}>
            <Typography fontSize={12} lineHeight={20} color={textColor} fontWeight={500}>
              {label}
            </Typography>
          </Badge>
        );
      },
    },
    {
      id: 'mail_tracking.recipient_type',
      width: '10%',
      Header: (props: any) => <CustomHeader tableProps={props} title="Recipient Type" />,
      accessor: 'recipientType',
      Cell: ({ value }) => {
        const label =
          value === MailTrackingRecipientType.SYSTEM_ADMIN ? 'System admin' : 'Organization admin';

        return (
          <Badge
            pill
            bg={value === MailTrackingRecipientType.SYSTEM_ADMIN ? 'primary' : 'secondary'}
          >
            <Typography
              fontSize={12}
              lineHeight={20}
              color={value === MailTrackingRecipientType.SYSTEM_ADMIN ? '#fff' : '#000'}
              fontWeight={500}
            >
              {label}
            </Typography>
          </Badge>
        );
      },
    },
    {
      id: 'mail_tracking.title',
      width: '30%',
      Header: (props: any) => <CustomHeader tableProps={props} title="Mail Content" />,
      accessor: 'title',
      Cell: ({ value, row }) => {
        const { templateContent } = row.original;

        const handleClickToShowContentModal = (event: React.MouseEvent<HTMLDivElement>) => {
          event.stopPropagation();
          setIsShow((prev) => ({ ...prev, content: true }));
          setData((prev) => ({ ...prev, content: templateContent, title: value }));
        };

        return (
          <>
            <Tippy content="Click to view full content" interactive theme="dark" placement="top">
              <MailContentWrapper onClick={handleClickToShowContentModal}>
                <Typography variant="body-1">{value}</Typography>
              </MailContentWrapper>
            </Tippy>
          </>
        );
      },
    },
    {
      id: 'recipients',
      width: '15%',
      Header: 'Sent To',
      accessor: 'recipients',
      Cell: ({ value }) => {
        const handleClickToShowRecipientModal = (event: React.MouseEvent<HTMLDivElement>) => {
          event.stopPropagation();
          setIsShow((prev) => ({ ...prev, recipient: true }));
          setData((prev) => ({ ...prev, recipient: value }));
        };

        return (
          <RecipientWrapper
            onClick={handleClickToShowRecipientModal}
            className="d-flex align-items-center gap-2"
          >
            <UserGroupIcon />
            <Typography variant="body-1" className="whitespace-nowrap">
              {value.length} {value.length > 1 ? 'recipients' : 'recipient'}
            </Typography>
          </RecipientWrapper>
        );
      },
    },
    {
      id: 'actions',
      width: '5%',
      Header: '',
      Cell: ({ ...props }) => (
        <ActionsCell
          item={props.data[props.row.index]}
          actions={actions(props.data[props.row.index])}
        />
      ),
    },
  ];
};

export const recipitentColumns: ReadonlyArray<Column<Recipient>> = [
  {
    id: 'email',
    width: '70%',
    Header: 'Email',
    accessor: 'email',
    Cell: ({ value }: { value: string }) => <Typography variant="body-1">{value}</Typography>,
  },
  {
    id: 'readAt',
    width: '10%',
    Header: 'Status',
    accessor: 'readAt',
    Cell: ({ value }) => {
      const isUnread = isNull(value);

      return (
        <Badge pill bg={isUnread ? 'danger' : 'success'}>
          <Typography fontSize={12} lineHeight={20} color="#fff" fontWeight={500}>
            {isUnread ? 'Unread' : 'Read'}
          </Typography>
        </Badge>
      );
    },
  },
  {
    id: '',
    width: '20%',
    Header: 'Read at',
    Cell: ({ row }) => (
      <Typography variant="body-2">{formatCustomDate(row.original.readAt as FormDate)}</Typography>
    ),
  },
];
